import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Building2, User, CreditCard, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface TenantDetailsDialogProps {
  tenant: any;
  adminInfo: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TenantDetailsDialog({
  tenant,
  adminInfo,
  open,
  onOpenChange,
}: TenantDetailsDialogProps) {
  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Detalhes da Tenant
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações da Empresa */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Building2 className="h-4 w-4" />
              INFORMAÇÕES DA EMPRESA
            </div>
            <div className="grid grid-cols-2 gap-4 pl-6">
              <div>
                <div className="text-xs text-muted-foreground">Nome</div>
                <div className="font-medium">{tenant.name}</div>
              </div>
              {tenant.cnpj && (
                <div>
                  <div className="text-xs text-muted-foreground">CNPJ</div>
                  <div className="font-medium">{tenant.cnpj}</div>
                </div>
              )}
              {tenant.email && (
                <div>
                  <div className="text-xs text-muted-foreground">E-mail</div>
                  <div className="font-medium">{tenant.email}</div>
                </div>
              )}
              {tenant.phone && (
                <div>
                  <div className="text-xs text-muted-foreground">Telefone</div>
                  <div className="font-medium">{tenant.phone}</div>
                </div>
              )}
              {tenant.domain && (
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground">Domínio</div>
                  <div className="font-medium">{tenant.domain}</div>
                </div>
              )}
            </div>

            {tenant.address_street && (
              <div className="pl-6">
                <div className="text-xs text-muted-foreground">Endereço</div>
                <div className="font-medium">
                  {tenant.address_street}, {tenant.address_number}
                  {tenant.address_complement && ` - ${tenant.address_complement}`}
                  <br />
                  {tenant.address_neighborhood}, {tenant.address_city} - {tenant.address_state}
                  {tenant.address_zipcode && ` - CEP ${tenant.address_zipcode}`}
                </div>
              </div>
            )}
          </div>

          {/* Administrador */}
          {adminInfo && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <User className="h-4 w-4" />
                ADMINISTRADOR
              </div>
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div>
                  <div className="text-xs text-muted-foreground">Nome</div>
                  <div className="font-medium">{adminInfo.admin_full_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">E-mail</div>
                  <div className="font-medium">{adminInfo.admin_email}</div>
                </div>
                {adminInfo.admin_phone && (
                  <div>
                    <div className="text-xs text-muted-foreground">Telefone</div>
                    <div className="font-medium">{adminInfo.admin_phone}</div>
                  </div>
                )}
                {adminInfo.last_access_at && (
                  <div>
                    <div className="text-xs text-muted-foreground">Último Acesso</div>
                    <div className="font-medium">
                      {format(new Date(adminInfo.last_access_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plano de Assinatura */}
          {tenant.subscription_plans && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                PLANO DE ASSINATURA
              </div>
              <div className="pl-6 space-y-2">
                <div>
                  <div className="font-medium text-lg">
                    {tenant.subscription_plans.name}
                    {tenant.subscription_plans.price_monthly && (
                      <span className="text-sm text-muted-foreground ml-2">
                        - R$ {tenant.subscription_plans.price_monthly}/mês
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              STATUS
            </div>
            <div className="pl-6 flex items-center gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <Badge variant={tenant.is_active ? "default" : "secondary"}>
                  {tenant.is_active ? "Ativo" : "Inativo"}
                </Badge>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Criado em</div>
                <div className="font-medium">
                  {format(new Date(tenant.created_at), "dd/MM/yyyy", { locale: ptBR })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
